/**
 * The Shopping Assistant pages' reading of their numbers, as pure functions.
 *
 * The pages lead with sentences — "the assistant helped 42 shoppers; 5
 * questions it could not answer need you" — and every one of those sentences
 * is decided here, from the three reports, so the rules can be tested without
 * rendering a page. What comes out is a key and its figures; the page turns it
 * into words in the vendor's language.
 */
import type {
    ConversationOutcome,
    ConversationSummary,
    InsightsRange,
    ShopperOverview,
    ShopperQuestionEntry,
    ShopperQuestions,
    ShopperQuestionTheme,
} from '../../../common/hooks/use-shopper-insights-api.hook';

export type ReadingTone = 'good' | 'bad' | 'watch' | 'neutral';

const DAY = 24 * 60 * 60 * 1000;
const toDay = (s: string) => Date.parse(`${s}T00:00:00.000Z`);
const fromDay = (t: number) => new Date(t).toISOString().slice(0, 10);

/** Days in a range, both ends included. */
export function daysIn(range: InsightsRange): number {
    const from = toDay(range.startDate);
    const to = toDay(range.endDate);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return 0;
    return Math.round((to - from) / DAY) + 1;
}

/** The same number of days, ending the day before `range` starts — what a change is measured against. */
export function previousRange(range: InsightsRange): InsightsRange {
    const days = Math.max(1, daysIn(range));
    const end = toDay(range.startDate) - DAY;
    return { startDate: fromDay(end - (days - 1) * DAY), endDate: fromDay(end) };
}

/** Change in percent; null when there is nothing to compare with. */
export function percentChange(current: number, previous: number | null | undefined): number | null {
    if (!previous) return null;
    return ((current - previous) / previous) * 100;
}

/** Share of replies the assistant could not give; null before it has replied at all. */
export function unansweredRate(totals: Pick<ShopperOverview['totals'], 'replies' | 'unanswered'> | null | undefined): number | null {
    if (!totals || !totals.replies) return null;
    return totals.unanswered / totals.replies;
}

/**
 * How a conversation ended. The API says so; an API from before it did is read
 * from the row's own counts, with the same priority.
 */
export function conversationOutcomeOf(row: Pick<ConversationSummary, 'outcome' | 'unanswered' | 'products' | 'productsShown'>): ConversationOutcome {
    if (row.outcome) return row.outcome;
    if (row.unanswered > 0) return 'unanswered';
    if ((row.productsShown ?? row.products?.length ?? 0) > 0) return 'recommended';
    return 'answered';
}

/** The order the outcomes are shown in: the good ones first, what needs the vendor last and in amber. */
export const OUTCOME_ORDER: ConversationOutcome[] = ['answered', 'recommended', 'room', 'unanswered'];

export interface OutcomeSplit {
    /** Conversations when the API splits them, replies from an older one. */
    unit: 'conversations' | 'replies';
    segments: Array<{ key: ConversationOutcome; value: number }>;
    total: number;
}

export function outcomeSplit(overview: ShopperOverview | null | undefined): OutcomeSplit | null {
    if (!overview) return null;
    const split = overview.conversationOutcomes;
    if (split) {
        const segments = OUTCOME_ORDER.map((key) => ({ key, value: split[key] || 0 }));
        return { unit: 'conversations', segments, total: segments.reduce((s, x) => s + x.value, 0) };
    }
    const { replies, unanswered } = overview.totals;
    const segments = [
        { key: 'answered' as const, value: Math.max(0, replies - unanswered) },
        { key: 'unanswered' as const, value: unanswered },
    ];
    return { unit: 'replies', segments, total: replies };
}

/** Why a reply did not come, in terms of what the vendor can do about it. */
export type UnansweredReason = 'no_match' | 'unknown' | 'failed' | 'no_reply';

export const reasonOf = (outcome: string | null | undefined): UnansweredReason =>
    outcome === 'no_results' ? 'no_match'
        // The assistant answered honestly that the shop hasn't said: something to
        // add to what it knows (Settings › Shopping Assistant › your store).
        : outcome === 'deflected' ? 'unknown'
        : outcome === 'no_reply' ? 'no_reply' : 'failed';

export interface NeedsYouItem {
    key: string;
    question: string;
    count: number;
    /** The latest time it was asked, and the conversation it was asked in. */
    lastAt: string;
    threadUuid: string;
    sku: string | null;
    reason: UnansweredReason;
}

const wording = (question: string) =>
    String(question || '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/[?!.]+$/, '');

/**
 * The questions it could not answer, once per wording, the most repeated first.
 *
 * A question asked nine times is nine shoppers the catalogue let down in the
 * same way — one fix — so it outranks nine different questions asked once.
 */
export function groupUnanswered(entries: ShopperQuestionEntry[] | null | undefined, limit = 5): NeedsYouItem[] {
    const groups = new Map<string, NeedsYouItem>();
    for (const entry of entries || []) {
        const key = wording(entry.question) || `(none):${entry.threadUuid}`;
        const known = groups.get(key);
        if (!known) {
            groups.set(key, {
                key,
                question: entry.question,
                count: 1,
                lastAt: entry.at,
                threadUuid: entry.threadUuid,
                sku: entry.sku,
                reason: reasonOf(entry.outcome),
            });
            continue;
        }
        known.count++;
        if (new Date(entry.at).getTime() > new Date(known.lastAt).getTime()) {
            known.lastAt = entry.at;
            known.threadUuid = entry.threadUuid;
            known.sku = entry.sku ?? known.sku;
        }
        // "No match" is the one the vendor can fix; keep it if any repeat said so.
        if (reasonOf(entry.outcome) === 'no_match') known.reason = 'no_match';
    }
    return [...groups.values()]
        .sort((a, b) => b.count - a.count || new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime())
        .slice(0, limit);
}

/**
 * A search the catalogue could not meet, as a shopper would say it.
 *
 * The API records the assistant's filters — "colour: Black, surface: floor,
 * query: zellige" — which is exact and unreadable. The values alone say it:
 * "Black · floor · “zellige”". Free text is quoted so it reads as words the
 * shopper used rather than a category.
 */
export function describeSearch(filters: string): string {
    const text = String(filters || '').trim();
    if (!text || text === '(no filters)') return '';
    const parts = text.split(/,\s*(?=[A-Za-z]+:)/).map((pair) => {
        const at = pair.indexOf(':');
        if (at < 0) return pair.trim();
        const field = pair.slice(0, at).trim();
        const value = pair.slice(at + 1).trim();
        return field === 'query' ? `“${value}”` : value;
    });
    return parts.filter(Boolean).join(' · ');
}

/** Themes as the pages list them: the largest first, and "other" always last. */
export function themeRows(questions: ShopperQuestions | null | undefined): ShopperQuestionTheme[] {
    const themes = [...(questions?.themes || [])].filter((t) => t.count > 0);
    return themes.sort((a, b) => (a.theme === 'other' ? 1 : 0) - (b.theme === 'other' ? 1 : 0) || b.count - a.count);
}

/** A theme is going well when at most one question in ten went unanswered. */
export const themeAnsweredWell = (theme: Pick<ShopperQuestionTheme, 'count' | 'unanswered'>) =>
    theme.count > 0 && theme.unanswered / theme.count <= 0.1;

export type ReadingAction =
    | { kind: 'link'; href: string; label: 'REVIEW' | 'SEE_CONVERSATIONS' | 'SEE_QUESTIONS' | 'CHECK_SETTINGS' }
    | { kind: 'ask'; question: string; sku: string | null };

export interface ReadingItem {
    id: string;
    tone: ReadingTone;
    /** Under SHOPPER_UI.READING — the title, and the detail line when there is one. */
    title: string;
    detail?: string;
    params: Record<string, string | number>;
    action?: ReadingAction;
}

/**
 * The plain-language reading at the top of the dashboard: at most five
 * sentences, most useful first — how many it helped, what needs the vendor,
 * the one question worth fixing, and the good news.
 */
export function assistantReading({
    overview,
    previous,
    questions,
    limit = 5,
}: {
    overview: ShopperOverview | null | undefined;
    previous?: ShopperOverview | null;
    questions?: ShopperQuestions | null;
    limit?: number;
}): ReadingItem[] {
    const totals = overview?.totals;
    if (!totals) return [];

    if (!totals.conversations) {
        return [{
            id: 'quiet',
            tone: 'neutral',
            title: 'QUIET',
            detail: 'QUIET_DETAIL',
            params: {},
            action: { kind: 'link', href: '/assistant/settings', label: 'CHECK_SETTINGS' },
        }];
    }

    const items: ReadingItem[] = [];

    const change = percentChange(totals.conversations, previous?.totals?.conversations);
    items.push({
        id: 'helped',
        tone: change === null ? 'neutral' : change >= 10 ? 'good' : change <= -10 ? 'watch' : 'neutral',
        title: 'HELPED',
        detail: change === null || Math.round(change) === 0 ? undefined : change > 0 ? 'HELPED_UP' : 'HELPED_DOWN',
        // `count` is what the sentence is plural on: the shoppers helped.
        params: { count: totals.shoppers, conversations: totals.conversations, change: Math.abs(Math.round(change ?? 0)) },
    });

    if (totals.unanswered > 0) {
        const rate = unansweredRate(totals);
        items.push({
            id: 'unanswered',
            tone: 'watch',
            title: 'UNANSWERED',
            detail: rate === null ? undefined : 'UNANSWERED_DETAIL',
            params: { count: totals.unanswered, rate: Math.round((rate ?? 0) * 100) },
            action: { kind: 'link', href: '/assistant/questions', label: 'REVIEW' },
        });
    }

    const repeated = groupUnanswered(questions?.unanswered, 1)[0];
    if (repeated && repeated.count >= 2 && repeated.question) {
        items.push({
            id: 'repeated',
            tone: 'watch',
            title: 'REPEATED',
            detail: repeated.reason === 'no_match' ? 'REPEATED_DETAIL' : 'REPEATED_FAILED_DETAIL',
            params: { question: repeated.question, count: repeated.count },
            action: { kind: 'ask', question: repeated.question, sku: repeated.sku },
        });
    }

    if (totals.feedbackDown > 0) {
        items.push({
            id: 'disliked',
            tone: 'bad',
            title: 'DISLIKED',
            params: { count: totals.feedbackDown },
            action: { kind: 'link', href: '/assistant/questions', label: 'SEE_QUESTIONS' },
        });
    }

    if (totals.cartClicks > 0) {
        items.push({
            id: 'cart',
            tone: 'good',
            title: 'CART',
            detail: totals.productClicks > 0 ? 'CART_DETAIL' : undefined,
            params: { count: totals.cartClicks, clicks: totals.productClicks },
        });
    }

    if (totals.renders > 0) {
        items.push({ id: 'room', tone: 'good', title: 'ROOM', params: { count: totals.renders } });
    }

    const theme = themeRows(questions).find((t) => t.theme !== 'other');
    const asked = (questions?.themes || []).reduce((s, t) => s + t.count, 0);
    if (theme && theme.count >= 3 && asked > 0) {
        items.push({
            id: 'theme',
            tone: 'neutral',
            title: 'TOP_THEME',
            params: { theme: theme.theme, count: theme.count, share: Math.round((theme.count / asked) * 100) },
            action: { kind: 'link', href: '/assistant/questions', label: 'SEE_QUESTIONS' },
        });
    }

    return items.slice(0, limit);
}
